// "Reported issues" — B4's triage queue on the loan Compliance tab. Lists
// every report on the loan (open first), opens the detail drawer via the
// ?report={id} param (deep-linkable from the cockpit + notifications), and
// hosts the triage modals. Any Loans.Read staff sees status (reporters
// follow their own reports here); only Compliance.ManageExceptions holders
// triage; the escalate action additionally requires Defaults.Manage and is
// hidden — never disabled — for non-holders.

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import type { ComplianceIntakeReportDto } from "@/api/compliance/intakeReportTypes";
import { useIntakeReports } from "@/hooks/compliance/useIntakeReports";
import { useDateFormatter } from "@/utils/datetime";
import {
  REPORT_STATUS_BADGE,
  ReportDetailDrawer,
  severityTone,
  type ReportTriageAction,
} from "./ReportDetailDrawer";
import {
  EscalateReportModal,
  ResolveReportModal,
  TriageStepModal,
} from "./ReportTriageModals";

const OPEN_STATUSES = ["New", "Acknowledged", "Investigating"] as const;

export function ReportedIssuesSection({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const canTriage =
    auth.hasPermission("Compliance.ManageExceptions") ||
    auth.hasRoleCode("ADMIN");
  const canEscalate =
    auth.hasPermission("Defaults.Manage") || auth.hasRoleCode("ADMIN");

  const reportsQuery = useIntakeReports(loanId);
  const { date } = useDateFormatter();

  const [params, setParams] = useSearchParams();
  const openReportId = params.get("report");
  const [modal, setModal] = useState<ReportTriageAction | null>(null);

  const reports = useMemo(() => {
    const rows = (reportsQuery.data ?? []).slice();
    const rank = (r: ComplianceIntakeReportDto) =>
      (OPEN_STATUSES as readonly string[]).includes(r.status) ? 0 : 1;
    return rows.sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (a.reportedAt < b.reportedAt ? 1 : a.reportedAt > b.reportedAt ? -1 : 0),
    );
  }, [reportsQuery.data]);

  const openReport =
    reports.find((r) => r.id === openReportId) ?? null;

  const setReportParam = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("report", id);
    else next.delete("report");
    setParams(next, { replace: true });
  };

  return (
    <section
      className="flex flex-col gap-space-2"
      data-testid="reported-issues-section"
    >
      <Card>
        <h3 className="font-display font-bold text-h3 m-0">Reported issues</h3>
        <p className="text-caption text-gray-mid mt-space-1 m-0">
          Problems reported to compliance from anywhere on the platform — B4
          triages each one: acknowledge → investigate → resolve or escalate.
        </p>
      </Card>

      {reportsQuery.isLoading ? (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            Loading reported issues…
          </p>
        </Card>
      ) : reportsQuery.isError ? (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            {extractErrorMessage(reportsQuery.error)}
          </p>
        </Card>
      ) : reports.length === 0 ? (
        <Card data-testid="reported-issues-empty">
          <p className="text-small text-gray-mid italic m-0">
            Nothing has been reported to compliance on this loan.
          </p>
        </Card>
      ) : (
        <ul
          aria-label="Reported issues"
          className="m-0 list-none divide-y divide-border-light bg-white-pure border border-border-light rounded-lg p-0"
          data-testid="reported-issues-list"
        >
          {reports.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setReportParam(r.id)}
                className="w-full text-left px-space-3 py-space-3 flex items-center gap-space-3 hover:bg-white-warm"
                data-testid={`reported-issue-row-${r.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-space-2 flex-wrap">
                    <Badge status={REPORT_STATUS_BADGE[r.status]}>
                      {r.status}
                    </Badge>
                    {r.source === "Borrower" && (
                      <Badge status="info">Borrower</Badge>
                    )}
                    <span className="font-display font-semibold text-small">
                      {r.category}
                    </span>
                    <span
                      className={`text-caption ${severityTone(
                        r.triageSeverity ?? r.suggestedSeverity,
                      )}`}
                    >
                      {r.triageSeverity ?? r.suggestedSeverity ?? "Unrated"}
                    </span>
                  </div>
                  <p className="text-caption text-gray-mid mt-space-1 m-0 truncate">
                    {r.description}
                  </p>
                  <p className="text-caption text-gray-mid mt-space-1 m-0">
                    Reported by {r.reportedByUserName ?? "Unknown user"} ·{" "}
                    {date(r.reportedAt)}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {openReport && !modal && (
        <ReportDetailDrawer
          report={openReport}
          canTriage={canTriage}
          canEscalate={canEscalate}
          onAction={setModal}
          onClose={() => setReportParam(null)}
        />
      )}

      {openReport && (modal === "acknowledge" || modal === "investigate") && (
        <TriageStepModal
          report={openReport}
          mode={modal}
          onClose={() => setModal(null)}
        />
      )}
      {openReport && modal === "resolve" && (
        <ResolveReportModal report={openReport} onClose={() => setModal(null)} />
      )}
      {openReport && modal === "escalate" && (
        <EscalateReportModal report={openReport} onClose={() => setModal(null)} />
      )}
    </section>
  );
}
